function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
var _resetTimeouts = _classPrivateFieldLooseKey("resetTimeouts");
var _preloadImage = _classPrivateFieldLooseKey("preloadImage");
var _animateFlyToCart = _classPrivateFieldLooseKey("animateFlyToCart");
export class AddToCartComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Animates the fly to cart animation.
     */
    Object.defineProperty(this, _animateFlyToCart, {
      value: _animateFlyToCart2
    });
    this.requiredRefs = ['addToCartButton'];
    /** @type {number[] | undefined} */
    Object.defineProperty(this, _resetTimeouts, {
      writable: true,
      value: (/** @type {number[]} */[])
    });
    Object.defineProperty(this, _preloadImage, {
      writable: true,
      value: () => {
        const image = this.dataset.productVariantMedia;
        if (!image) return;
        preloadImage(image);
      }
    });
    /**
     * Animates the add to cart button.
     */
    this.animateAddToCart = async function () {
      const {
        addToCartButton
      } = this.refs;

      // Initialize the array if it doesn't exist
      if (!_classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts]) {
        _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts] = [];
      }

      // Clear all existing timeouts
      _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts].forEach(/** @param {number} timeoutId */timeoutId => clearTimeout(timeoutId));
      _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts] = [];
      if (addToCartButton.dataset.added !== 'true') {
        addToCartButton.dataset.added = 'true';
      }

      // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
      await yieldToMainThread();
      await onAnimationEnd(addToCartButton);

      // Create new timeout and store it in the array
      const timeoutId = setTimeout(() => {
        addToCartButton.removeAttribute('data-added');

        // Remove this timeout from the array
        const index = _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts].indexOf(timeoutId);
        if (index > -1) {
          _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts].splice(index, 1);
        }
      }, 800);
      _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts].push(timeoutId);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', _classPrivateFieldLooseBase(this, _preloadImage)[_preloadImage]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (_classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts]) {
      _classPrivateFieldLooseBase(this, _resetTimeouts)[_resetTimeouts].forEach(/** @param {number} timeoutId */timeoutId => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', _classPrivateFieldLooseBase(this, _preloadImage)[_preloadImage]);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */this.closest('product-form-component');
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        _classPrivateFieldLooseBase(this, _animateFlyToCart)[_animateFlyToCart]();
      }
      this.animateAddToCart();
    }
  }
}
function _animateFlyToCart2() {
  const {
    addToCartButton
  } = this.refs;
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const image = this.dataset.productVariantMedia;
  if (!cartIcon || !addToCartButton || !image) return;
  const flyToCartElement = /** @type {FlyToCart} */document.createElement('fly-to-cart');
  let flyToCartClass = addToCartButton.classList.contains('quick-add__button') ? 'fly-to-cart--quick' : 'fly-to-cart--main';
  flyToCartElement.classList.add(flyToCartClass);
  flyToCartElement.style.setProperty('background-image', `url(${image})`);
  flyToCartElement.style.setProperty('--start-opacity', '0');
  flyToCartElement.source = addToCartButton;
  flyToCartElement.destination = cartIcon;
  document.body.appendChild(flyToCartElement);
}
if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
var _abortController = _classPrivateFieldLooseKey("abortController");
var _timeout = _classPrivateFieldLooseKey("timeout");
var _variantChangeInProgress = _classPrivateFieldLooseKey("variantChangeInProgress");
var _addToCartQueue = _classPrivateFieldLooseKey("addToCartQueue");
var _updateCartQuantityFromData = _classPrivateFieldLooseKey("updateCartQuantityFromData");
var _fetchAndUpdateCartQuantity = _classPrivateFieldLooseKey("fetchAndUpdateCartQuantity");
var _onCartUpdate = _classPrivateFieldLooseKey("onCartUpdate");
var _getIntendedVariantId = _classPrivateFieldLooseKey("getIntendedVariantId");
var _getQuantity = _classPrivateFieldLooseKey("getQuantity");
var _shouldRedirectToCartPage = _classPrivateFieldLooseKey("shouldRedirectToCartPage");
var _processAddToCart = _classPrivateFieldLooseKey("processAddToCart");
var _processBatchAddToCart = _classPrivateFieldLooseKey("processBatchAddToCart");
var _updateQuantityLabel = _classPrivateFieldLooseKey("updateQuantityLabel");
var _setLiveRegionText = _classPrivateFieldLooseKey("setLiveRegionText");
var _clearLiveRegionText = _classPrivateFieldLooseKey("clearLiveRegionText");
var _morphOrUpdateElement = _classPrivateFieldLooseKey("morphOrUpdateElement");
var _onVariantUpdate = _classPrivateFieldLooseKey("onVariantUpdate");
var _onVariantSelected = _classPrivateFieldLooseKey("onVariantSelected");
class ProductFormComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Morphs or removes/adds an element based on current and new element states
     * @param {Element | null | undefined} currentElement - The current element in the DOM
     * @param {Element | null | undefined} newElement - The new element from the server response
     * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
     */
    Object.defineProperty(this, _morphOrUpdateElement, {
      value: _morphOrUpdateElement2
    });
    Object.defineProperty(this, _clearLiveRegionText, {
      value: _clearLiveRegionText2
    });
    /**
     * @param {*} text
     */
    Object.defineProperty(this, _setLiveRegionText, {
      value: _setLiveRegionText2
    });
    /**
     * Updates the quantity label with the current cart quantity
     * @param {number} cartQty - The quantity in cart
     */
    Object.defineProperty(this, _updateQuantityLabel, {
      value: _updateQuantityLabel2
    });
    /** @param {Array<{variantId: string, quantity: number}>} items */
    Object.defineProperty(this, _processBatchAddToCart, {
      value: _processBatchAddToCart2
    });
    /**
     * @param {string} [overrideVariantId]
     * @param {number} [overrideQuantity]
     * @param {Event} [event]
     */
    Object.defineProperty(this, _processAddToCart, {
      value: _processAddToCart2
    });
    /**
     * Tam sayfa sepet (/cart) yönlendirmesi: add-to-cart bileşeninde data-redirect-to-cart="true".
     * @param {Event | undefined} event
     * @returns {boolean}
     */
    Object.defineProperty(this, _shouldRedirectToCartPage, {
      value: _shouldRedirectToCartPage2
    });
    /** @returns {number} */
    Object.defineProperty(this, _getQuantity, {
      value: _getQuantity2
    });
    /** @returns {string | undefined} */
    Object.defineProperty(this, _getIntendedVariantId, {
      value: _getIntendedVariantId2
    });
    /**
     * Fetches cart and updates quantity selector for current variant
     * @returns {Promise<number>} The cart quantity for the current variant
     */
    Object.defineProperty(this, _fetchAndUpdateCartQuantity, {
      value: _fetchAndUpdateCartQuantity2
    });
    /**
     * Updates quantity selector with cart data for current variant
     * @param {Cart} cart - The cart object with items array
     * @returns {number} The cart quantity for the current variant
     */
    Object.defineProperty(this, _updateCartQuantityFromData, {
      value: _updateCartQuantityFromData2
    });
    this.requiredRefs = ['variantId', 'liveRegion'];
    Object.defineProperty(this, _abortController, {
      writable: true,
      value: new AbortController()
    });
    /** @type {number | undefined} */
    Object.defineProperty(this, _timeout, {
      writable: true,
      value: void 0
    });
    /** @type {boolean} */
    Object.defineProperty(this, _variantChangeInProgress, {
      writable: true,
      value: false
    });
    /** @type {Array<{variantId: string, quantity: number}>} */
    Object.defineProperty(this, _addToCartQueue, {
      writable: true,
      value: []
    });
    /**
     * Updates data-cart-quantity when cart is updated from elsewhere
     * @param {CartUpdateEvent|CartAddEvent} event
     */
    Object.defineProperty(this, _onCartUpdate, {
      writable: true,
      value: async event => {
        // Skip if this event came from this component
        if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;
        const cart = /** @type {Cart} */event.detail?.resource;
        if (cart?.items) {
          _classPrivateFieldLooseBase(this, _updateCartQuantityFromData)[_updateCartQuantityFromData](cart);
        } else {
          await _classPrivateFieldLooseBase(this, _fetchAndUpdateCartQuantity)[_fetchAndUpdateCartQuantity]();
        }
      }
    });
    /**
     * @param {VariantUpdateEvent} event
     */
    Object.defineProperty(this, _onVariantUpdate, {
      writable: true,
      value: async event => {
        if (event.detail.data.newProduct) {
          this.dataset.productId = event.detail.data.newProduct.id;
        } else if (event.detail.data.productId !== this.dataset.productId) {
          return;
        }
        const {
          variantId
        } = this.refs;
        variantId.value = event.detail.resource?.id ?? '';
        _classPrivateFieldLooseBase(this, _variantChangeInProgress)[_variantChangeInProgress] = false;
        if (_classPrivateFieldLooseBase(this, _addToCartQueue)[_addToCartQueue].length > 0) {
          const queuedItems = [..._classPrivateFieldLooseBase(this, _addToCartQueue)[_addToCartQueue]];
          _classPrivateFieldLooseBase(this, _addToCartQueue)[_addToCartQueue] = [];
          _classPrivateFieldLooseBase(this, _processBatchAddToCart)[_processBatchAddToCart](queuedItems);
        }
        const {
          addToCartButtonContainer: currentAddToCartButtonContainer,
          acceleratedCheckoutButtonContainer
        } = this.refs;
        const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

        // Update state and text for add-to-cart button
        if (!currentAddToCartButtonContainer || !currentAddToCartButton && !acceleratedCheckoutButtonContainer) return;

        // Update the button state
        if (event.detail.resource == null || event.detail.resource.available == false) {
          currentAddToCartButtonContainer.disable();
        } else {
          currentAddToCartButtonContainer.enable();
        }

        // Birden fazla buy-buttons / product-form olduğunda yalnızca ilk buton seçilmesin: aynı id ile eşleştir.
        const newAddToCartButton = currentAddToCartButton?.id ? event.detail.data.html.querySelector(`#${CSS.escape(currentAddToCartButton.id)}`) : event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
        if (newAddToCartButton && currentAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }
        if (acceleratedCheckoutButtonContainer) {
          if (event.detail.resource == null || event.detail.resource.available == false) {
            acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
          } else {
            acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
          }
        }

        // Set the data attribute for the product variant media if it exists
        if (event.detail.resource) {
          const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
          if (productVariantMedia) {
            this.refs.addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
          }
        }

        // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
        const {
          quantityRules,
          pricePerItem,
          quantitySelector,
          productFormButtons,
          quantityLabel,
          quantitySelectorWrapper
        } = this.refs;

        // Update quantity selector's min/max/step attributes and cart quantity for the new variant
        const newQuantityInput = /** @type {HTMLInputElement | null} */
        event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');
        if (quantitySelector?.updateConstraints && newQuantityInput) {
          quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        }
        const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
        const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;
        const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
        const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;
        if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
          // Store quantity value before morphing entire container
          const currentQuantityValue = quantitySelector.getValue?.();
          const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');
          if (productFormButtons && newProductFormButtons) {
            morph(productFormButtons, newProductFormButtons);

            // Get the NEW quantity selector after morphing and update its constraints
            const newQuantityInputElement = /** @type {HTMLInputElement | null} */
            event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]');
            if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
              // Temporarily set the old value so updateConstraints can snap it properly
              this.refs.quantitySelector.setValue(currentQuantityValue);
              // updateConstraints will snap to valid increment if needed
              this.refs.quantitySelector.updateConstraints(newQuantityInputElement.min, newQuantityInputElement.max || null, newQuantityInputElement.step);
            }
          }
        } else {
          // Update elements individually when layout isn't changing
          /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
          const morphTargets = [['.quantity-label', quantityLabel, quantitySelector], ['.quantity-rules', quantityRules, this.refs.productFormButtons], ['price-per-item', pricePerItem, quantitySelectorWrapper]];
          for (const [selector, currentElement, fallback] of morphTargets) {
            _classPrivateFieldLooseBase(this, _morphOrUpdateElement)[_morphOrUpdateElement](currentElement, event.detail.data.html.querySelector(selector), fallback);
          }
        }

        // Morph volume pricing if it exists
        const currentVolumePricing = this.refs.volumePricing;
        const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
        _classPrivateFieldLooseBase(this, _morphOrUpdateElement)[_morphOrUpdateElement](currentVolumePricing, newVolumePricing, this.refs.productFormButtons);
        const hasB2BFeatures = quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;
        if (!hasB2BFeatures) return;

        // Fetch and update cart quantity for the new variant
        await _classPrivateFieldLooseBase(this, _fetchAndUpdateCartQuantity)[_fetchAndUpdateCartQuantity]();
      }
    });
    /** @param {import('./events').VariantSelectedEvent} _event */
    Object.defineProperty(this, _onVariantSelected, {
      writable: true,
      value: _event => {
        _classPrivateFieldLooseBase(this, _variantChangeInProgress)[_variantChangeInProgress] = true;
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    const {
      signal
    } = _classPrivateFieldLooseBase(this, _abortController)[_abortController];
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, _classPrivateFieldLooseBase(this, _onVariantUpdate)[_onVariantUpdate], {
      signal
    });
    target?.addEventListener(ThemeEvents.variantSelected, _classPrivateFieldLooseBase(this, _onVariantSelected)[_onVariantSelected], {
      signal
    });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, _classPrivateFieldLooseBase(this, _onCartUpdate)[_onCartUpdate], {
      signal
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abortController)[_abortController].abort();
  }
  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    if (_classPrivateFieldLooseBase(this, _variantChangeInProgress)[_variantChangeInProgress]) {
      const intendedVariantId = _classPrivateFieldLooseBase(this, _getIntendedVariantId)[_getIntendedVariantId]();
      const quantity = _classPrivateFieldLooseBase(this, _getQuantity)[_getQuantity]();
      if (intendedVariantId) {
        _classPrivateFieldLooseBase(this, _addToCartQueue)[_addToCartQueue].push({
          variantId: intendedVariantId,
          quantity
        });
      }
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }
    _classPrivateFieldLooseBase(this, _processAddToCart)[_processAddToCart](undefined, undefined, event);
  }
}
function _updateCartQuantityFromData2(cart) {
  const variantIdInput = /** @type {HTMLInputElement | null} */this.querySelector('input[name="id"]');
  if (!variantIdInput?.value || !cart?.items) return 0;
  const cartItem = cart.items.find(item => item.variant_id.toString() === variantIdInput.value.toString());
  const cartQty = cartItem ? cartItem.quantity : 0;

  // Use public API to update quantity selector
  const quantitySelector = /** @type {any | undefined} */this.querySelector('quantity-selector-component');
  if (quantitySelector?.setCartQuantity) {
    quantitySelector.setCartQuantity(cartQty);
  }

  // Update quantity label if it exists
  _classPrivateFieldLooseBase(this, _updateQuantityLabel)[_updateQuantityLabel](cartQty);
  return cartQty;
}
async function _fetchAndUpdateCartQuantity2() {
  const variantIdInput = /** @type {HTMLInputElement | null} */this.querySelector('input[name="id"]');
  if (!variantIdInput?.value) return 0;
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    return _classPrivateFieldLooseBase(this, _updateCartQuantityFromData)[_updateCartQuantityFromData](cart);
  } catch (error) {
    console.error('Failed to fetch cart quantity:', error);
    return 0;
  }
}
function _getIntendedVariantId2() {
  return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
}
function _getQuantity2() {
  return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
}
function _shouldRedirectToCartPage2(event) {
  const submitter = event && 'submitter' in event ? /** @type {SubmitEvent} */event.submitter : null;
  if (!submitter || !(submitter instanceof HTMLElement)) return false;
  const comp = submitter.closest('add-to-cart-component');
  return comp?.getAttribute('data-redirect-to-cart') === 'true';
}
function _processAddToCart2(overrideVariantId, overrideQuantity, event) {
  const {
    addToCartTextError
  } = this.refs;
  if (_classPrivateFieldLooseBase(this, _timeout)[_timeout]) clearTimeout(_classPrivateFieldLooseBase(this, _timeout)[_timeout]);
  const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */
  this.querySelectorAll('add-to-cart-component');
  if (!overrideVariantId) {
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(container => container.refs.addToCartButton?.disabled);
    if (anyButtonDisabled) return;
  }
  const form = this.querySelector('form');
  if (!form) throw new Error('Product form element missing');
  if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
    const validation = this.refs.quantitySelector.canAddToCart();
    if (!validation.canAdd) {
      for (const container of allAddToCartContainers) {
        container.disable();
      }
      const errorTemplate = this.dataset.quantityErrorMax || '';
      const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          const newTextNode = document.createTextNode(errorMessage);
          addToCartTextError.appendChild(newTextNode);
        }
        _classPrivateFieldLooseBase(this, _setLiveRegionText)[_setLiveRegionText](errorMessage);
        if (_classPrivateFieldLooseBase(this, _timeout)[_timeout]) clearTimeout(_classPrivateFieldLooseBase(this, _timeout)[_timeout]);
        _classPrivateFieldLooseBase(this, _timeout)[_timeout] = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');
          _classPrivateFieldLooseBase(this, _clearLiveRegionText)[_clearLiveRegionText]();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }
      setTimeout(() => {
        for (const container of allAddToCartContainers) {
          container.enable();
        }
      }, ERROR_BUTTON_REENABLE_DELAY);
      return;
    }
  }
  const formData = new FormData(form);
  if (overrideVariantId) {
    formData.set('id', overrideVariantId);
  }
  if (overrideQuantity !== undefined) {
    formData.set('quantity', overrideQuantity.toString());
  }
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  let cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach(item => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
    formData.append('sections', cartItemComponentsSectionIds.join(','));
  });
  const fetchCfg = fetchConfig('javascript', {
    body: formData
  });
  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'text/html'
    }
  }).then(response => response.json()).then(async response => {
    if (response.status) {
      this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors));
      if (!addToCartTextError) return;
      addToCartTextError.classList.remove('hidden');

      // Reuse the text node if the user is spam-clicking
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = response.message;
      } else {
        const newTextNode = document.createTextNode(response.message);
        addToCartTextError.appendChild(newTextNode);
      }

      // Create or get existing error live region for screen readers
      _classPrivateFieldLooseBase(this, _setLiveRegionText)[_setLiveRegionText](response.message);
      _classPrivateFieldLooseBase(this, _timeout)[_timeout] = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');

        // Clear the announcement
        _classPrivateFieldLooseBase(this, _clearLiveRegionText)[_clearLiveRegionText]();
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
      // because our back-end still adds the max allowed amount to the cart.
      this.dispatchEvent(new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId
      }));
      return;
    } else {
      const id = formData.get('id');
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }
      if (!id) throw new Error('Form ID is required');

      // Add aria-live region to inform screen readers that the item was added
      // Get the added text from any add-to-cart button
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        _classPrivateFieldLooseBase(this, _setLiveRegionText)[_setLiveRegionText](addedText);
        setTimeout(() => {
          _classPrivateFieldLooseBase(this, _clearLiveRegionText)[_clearLiveRegionText]();
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      // Fetch the updated cart to get the actual total quantity for this variant
      await _classPrivateFieldLooseBase(this, _fetchAndUpdateCartQuantity)[_fetchAndUpdateCartQuantity]();
      const redirectToCart = _classPrivateFieldLooseBase(this, _shouldRedirectToCartPage)[_shouldRedirectToCartPage](event);
      this.dispatchEvent(new CartAddEvent({}, id.toString(), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
        sections: response.sections,
        ...(redirectToCart ? {
          skipCartDrawer: true
        } : {})
      }));
      if (redirectToCart && Theme.routes?.cart_url) {
        window.location.assign(Theme.routes.cart_url);
        return;
      }
    }
  }).catch(error => {
    console.error(error);
  }).finally(() => {
    if (event) {
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  });
}
function _processBatchAddToCart2(items) {
  if (items.length === 0) return;
  const {
    addToCartTextError
  } = this.refs;
  if (_classPrivateFieldLooseBase(this, _timeout)[_timeout]) clearTimeout(_classPrivateFieldLooseBase(this, _timeout)[_timeout]);
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const cartItemComponentsSectionIds = [];
  for (const item of cartItemsComponents) {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  }
  const payload = {
    items: items.map(item => ({
      id: Number(item.variantId),
      quantity: item.quantity
    })),
    sections: cartItemComponentsSectionIds.join(',')
  };
  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload)
  }).then(response => response.json()).then(async response => {
    if (response.status) {
      this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = response.message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(response.message));
        }
        _classPrivateFieldLooseBase(this, _setLiveRegionText)[_setLiveRegionText](response.message);
        _classPrivateFieldLooseBase(this, _timeout)[_timeout] = setTimeout(() => {
          addToCartTextError.classList.add('hidden');
          _classPrivateFieldLooseBase(this, _clearLiveRegionText)[_clearLiveRegionText]();
        }, ERROR_MESSAGE_DISPLAY_DURATION);
      }
      this.dispatchEvent(new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        productId: this.dataset.productId
      }));
      return;
    }
    if (addToCartTextError) {
      addToCartTextError.classList.add('hidden');
      addToCartTextError.removeAttribute('aria-live');
    }
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */
    this.querySelectorAll('add-to-cart-component');
    const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
    if (anyAddToCartButton) {
      const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
      const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
      _classPrivateFieldLooseBase(this, _setLiveRegionText)[_setLiveRegionText](addedText);
      setTimeout(() => _classPrivateFieldLooseBase(this, _clearLiveRegionText)[_clearLiveRegionText](), SUCCESS_MESSAGE_DISPLAY_DURATION);
    }
    await _classPrivateFieldLooseBase(this, _fetchAndUpdateCartQuantity)[_fetchAndUpdateCartQuantity]();
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    this.dispatchEvent(new CartAddEvent({}, this.id, {
      source: 'product-form-component',
      itemCount: totalQuantity,
      productId: this.dataset.productId,
      sections: response.sections
    }));
  }).catch(error => {
    console.error(error);
  });
}
function _updateQuantityLabel2(cartQty) {
  const quantityLabel = this.refs.quantityLabelCartCount;
  if (quantityLabel) {
    const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
    if (inCartText && inCartText[2]) {
      quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
    }

    // Show/hide based on quantity
    quantityLabel.classList.toggle('hidden', cartQty === 0);
  }
}
function _setLiveRegionText2(text) {
  const liveRegion = this.refs.liveRegion;
  liveRegion.textContent = text;
}
function _clearLiveRegionText2() {
  const liveRegion = this.refs.liveRegion;
  liveRegion.textContent = '';
}
function _morphOrUpdateElement2(currentElement, newElement, insertReferenceElement = null) {
  if (currentElement && newElement) {
    morph(currentElement, newElement);
  } else if (currentElement && !newElement) {
    currentElement.remove();
  } else if (!currentElement && newElement && insertReferenceElement) {
    insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */newElement.cloneNode(true));
  }
}
if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
